import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import CalendarPage from "@/pages/Calendar";
import Todos from "@/pages/Todos";
import TodoSchedule from "@/pages/TodoSchedule";
import Settings from "@/pages/Settings";
import ProjectDetail from "@/pages/ProjectDetail";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import WorkOrders from "@/pages/WorkOrders";
import LeadPipeline from "@/pages/LeadPipeline";
import Subscriptions from "@/pages/Subscriptions";
import Deliverables from "@/pages/Deliverables";
import ReadMe from "@/pages/ReadMe";
import Successes from "@/pages/Successes";
import InspirationBoard from "@/pages/InspirationBoard";

function Loading() {
  return <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
}

const ADMIN_ROLES = ["admin", "viewer"];
const README_ROLES = ["admin", "viewer", "readme_author"];

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !ADMIN_ROLES.includes(user.role)) {
    return <Navigate to={user.role === "readme_author" ? "/read-me" : "/tickets"} replace />;
  }
  return <Layout>{children}</Layout>;
}

function ReadmeRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!README_ROLES.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function ClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "readme_author") return <Navigate to="/read-me" replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "readme_author") return <Navigate to="/read-me" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/tickets" replace />;
  return <Layout><Dashboard /></Layout>;
}

function AppRouter() {
  const location = useLocation();
  // Re-establish auth after the Calendar OAuth round-trip (top-level context
  // has a different storage partition than the iframe the user came from).
  if (location.hash?.includes("st=")) {
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const tok = params.get("st");
    if (tok) {
      localStorage.setItem("dt_session_token", tok);
      window.history.replaceState({}, "", location.pathname + location.search);
      window.location.reload();
      return null;
    }
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/work-orders" element={<ProtectedRoute adminOnly><WorkOrders /></ProtectedRoute>} />
      <Route path="/work-orders/:clientId" element={<ProtectedRoute adminOnly><WorkOrders /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute adminOnly><Projects /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute adminOnly><ProjectDetail /></ProtectedRoute>} />
      <Route path="/lead-pipeline" element={<ProtectedRoute adminOnly><LeadPipeline /></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute adminOnly><Subscriptions /></ProtectedRoute>} />
      <Route path="/deliverables" element={<ProtectedRoute adminOnly><Deliverables /></ProtectedRoute>} />
      <Route path="/successes" element={<ProtectedRoute adminOnly><Successes /></ProtectedRoute>} />
      <Route path="/read-me" element={<ReadmeRoute><ReadMe /></ReadmeRoute>} />
      <Route path="/calendar" element={<ProtectedRoute adminOnly><CalendarPage /></ProtectedRoute>} />
      <Route path="/todos" element={<ProtectedRoute adminOnly><Todos /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute adminOnly><TodoSchedule /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/tickets" element={<ClientRoute><Tickets /></ClientRoute>} />
      <Route path="/tickets/:id" element={<ClientRoute><TicketDetail /></ClientRoute>} />
      <Route path="/inspiration" element={<ClientRoute><InspirationBoard /></ClientRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
